"use client";

import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicReviewMedia } from "@/actions/review";
import { AppImage as Image } from "@/components/ui/app-image";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { HlsVideo } from "@/components/ui/hls-video";
import { registerNativeModalBack } from "@/lib/ui/native-modal-back";
import { cn } from "@/lib/utils";

type ReviewMediaGridProps = {
  media: PublicReviewMedia[];
  reviewerName: string;
  className?: string;
};

const MAX_TILES = 6;

export function ReviewMediaGrid({
  media,
  reviewerName,
  className,
}: ReviewMediaGridProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = useCallback(() => setActiveIndex(null), []);
  const step = useCallback(
    (delta: number) =>
      setActiveIndex((current) => {
        if (current === null) return current;
        const next = (current + delta + media.length) % media.length;
        return next;
      }),
    [media.length],
  );

  if (media.length === 0) return null;

  const tiles = media.slice(0, MAX_TILES);
  const overflow = media.length - tiles.length;

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-2 gap-2 sm:grid-cols-3",
          media.length === 1 && "grid-cols-1 sm:grid-cols-1",
          className,
        )}
      >
        {tiles.map((item, index) => {
          const src =
            item.kind === "video" ? item.posterUrl || item.url : item.url;
          if (!src) return null;
          const isLastVisible = index === tiles.length - 1 && overflow > 0;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`Open ${reviewerName}'s review media ${index + 1}`}
              className={cn(
                "group relative block overflow-hidden rounded-2xl border border-border bg-surface-alt transition outline-none focus-visible:ring-2 focus-visible:ring-brand/50 active:scale-[0.99] dark:border-glass-border-10 dark:bg-glass-bg-05",
                media.length === 1 ? "aspect-video" : "aspect-square",
              )}
            >
              <Image
                src={src}
                alt={
                  item.altText || `${reviewerName} review media ${index + 1}`
                }
                fill
                sizes="(max-width: 640px) 50vw, 240px"
                className="object-cover transition duration-300 group-hover:scale-[1.04]"
              />
              {item.kind === "video" ? (
                <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 backdrop-blur">
                    <Play className="ml-0.5 h-5 w-5 fill-current" />
                  </span>
                </span>
              ) : null}
              {isLastVisible ? (
                <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-xl font-semibold text-white backdrop-blur-[1px]">
                  +{overflow}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <ReviewMediaLightbox
        media={media}
        reviewerName={reviewerName}
        activeIndex={activeIndex}
        onClose={close}
        onStep={step}
      />
    </>
  );
}

function ReviewMediaLightbox({
  media,
  reviewerName,
  activeIndex,
  onClose,
  onStep,
}: {
  media: PublicReviewMedia[];
  reviewerName: string;
  activeIndex: number | null;
  onClose: () => void;
  onStep: (delta: number) => void;
}) {
  const open = activeIndex !== null;
  const active = activeIndex !== null ? media[activeIndex] : null;
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") onStep(1);
      if (event.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onStep]);

  useEffect(() => {
    if (!open) return;
    return registerNativeModalBack(onClose);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onNativeBack = (event: Event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("hiive:native-back", onNativeBack, true);
    return () =>
      window.removeEventListener("hiive:native-back", onNativeBack, true);
  }, [open, onClose]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        layer={20000}
        showCloseButton={false}
        className="flex h-[100dvh] max-h-[100dvh] w-screen max-w-none items-center justify-center rounded-none border-none bg-black/95 p-0 text-white shadow-none"
      >
        <DialogTitle className="sr-only">
          {reviewerName} review media viewer
        </DialogTitle>

        {active ? (
          <div
            className="relative flex h-full w-full items-center justify-center px-4 py-14"
            onTouchStart={(event) => {
              const touch = event.touches[0];
              touchStart.current = touch
                ? { x: touch.clientX, y: touch.clientY }
                : null;
            }}
            onTouchEnd={(event) => {
              const start = touchStart.current;
              touchStart.current = null;
              const touch = event.changedTouches[0];
              if (!start || !touch) return;
              const dx = touch.clientX - start.x;
              const dy = touch.clientY - start.y;
              if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                onStep(dx < 0 ? 1 : -1);
              }
            }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close review media"
              className="absolute top-[max(env(safe-area-inset-top),1rem)] right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25"
            >
              <X className="h-6 w-6" />
            </button>
            {active.kind === "video" && active.url ? (
              <HlsVideo
                key={active.id}
                src={active.url}
                poster={active.posterUrl ?? undefined}
                controls
                autoPlay
                className="max-h-full max-w-full rounded-xl"
              />
            ) : active.url ? (
              <div className="relative h-full w-full">
                <Image
                  key={active.id}
                  src={active.url}
                  alt={active.altText || `${reviewerName} review media`}
                  fill
                  sizes="100vw"
                  className="object-contain"
                />
              </div>
            ) : null}

            {media.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => onStep(-1)}
                  aria-label="Previous media"
                  className="absolute top-1/2 left-3 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/60 active:scale-95"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={() => onStep(1)}
                  aria-label="Next media"
                  className="absolute top-1/2 right-3 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/60 active:scale-95"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <span className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-sm font-medium text-white backdrop-blur">
                  {(activeIndex ?? 0) + 1} / {media.length}
                </span>
              </>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
